import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';
import { resendService } from '../services/resendService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  margin-top: -80px;
  padding-top: 80px;
`;

const ContentSection = styled.section`
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
  text-align: center;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.lg};
  color: ${theme.colors.neutral.grey};
  text-align: center;
  margin-bottom: 40px;
`;

const FormCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 40px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 24px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${theme.colors.neutral.lightGrey};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.md};
  color: ${theme.colors.text.primary};
  transition: border-color ${theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: #FFD700;
  }
  
  &::placeholder {
    color: ${theme.colors.neutral.grey};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${theme.colors.neutral.lightGrey};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.md};
  color: ${theme.colors.text.primary};
  background-color: white;
  cursor: pointer;
  transition: border-color ${theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: #FFD700;
  }
`;

const RadioGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid ${theme.colors.neutral.lightGrey};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  
  &:hover {
    border-color: #FFD700;
    background-color: #fffaf0;
  }
  
  input:checked + & {
    border-color: #FFD700;
    background-color: #fffaf0;
  }
`;

const RadioInput = styled.input`
  margin-right: ${theme.spacing.sm};
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #FFD700;
  color: #232B3B;
  font-weight: ${theme.typography.fontWeights.bold};
  padding: 14px 20px;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.lg};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast};
  
  &:hover {
    background-color: #e6c200;
  }
  
  &:disabled {
    background-color: ${theme.colors.neutral.lightGrey};
    color: ${theme.colors.neutral.grey};
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  margin: 40px 0;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background-color: ${theme.colors.neutral.lightGrey};
  }
`;

const DividerText = styled.span`
  background-color: white;
  padding: 0 20px;
  position: relative;
  color: ${theme.colors.neutral.grey};
  font-style: italic;
`;

const AlternativeSection = styled.div`
  background-color: #f9f9f9;
  border-radius: ${theme.borderRadius.md};
  padding: 24px;
  margin-top: 32px;
`;

const AlternativeTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const QuestionBox = styled.div`
  background-color: white;
  border: 1px solid ${theme.colors.neutral.lightGrey};
  border-radius: ${theme.borderRadius.md};
  padding: 16px;
  position: relative;
  margin-bottom: ${theme.spacing.md};
`;

const QuestionText = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.neutral.darkGrey};
  font-style: italic;
  line-height: 1.6;
  padding-right: 50px;
`;

const CopyButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 8px;
  background-color: #FFD700;
  color: #232B3B;
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #e6c200;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const CopyTooltip = styled.span<{ show: boolean }>`
  position: absolute;
  top: -30px;
  right: 0;
  background-color: #232B3B;
  color: white;
  padding: 4px 8px;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sizes.xs};
  white-space: nowrap;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity ${theme.transitions.fast};
  pointer-events: none;
`;

const Analyze: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company: '',
    productName: '',
    purchaseYear: '',
    annuityType: '',
    email: ''
  });
  const [showTooltip, setShowTooltip] = useState(false);

  const companies = [
    'Select a company...',
    'Allianz',
    'Athene',
    'AIG',
    'Brighthouse',
    'Equitable',
    'F&G',
    'Jackson National',
    'Lincoln Financial',
    'Nationwide',
    'Pacific Life',
    'Prudential',
    'Other'
  ];

  const years = ['Select a year...'];
  for (let year = 2025; year >= 2010; year--) {
    years.push(year.toString());
  }

  const quintessentialQuestion = `"Could you backtest the 10-year historical performance of the [insert annuity name] structured annuity product, specifically analyzing how the managed volatility component affected real returns versus the underlying index (S&P 500), and show me how the date lottery (monthly point-to-point crediting) would have impacted investor outcomes based on different start dates?"`;

  const handleCopyQuestion = () => {
    navigator.clipboard.writeText(quintessentialQuestion);
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Send admin notification email
      await resendService.sendEmail({
        to: 'jake@averillagencies.com',
        subject: '🔍 New Annuity Analysis Request',
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto;">
            <div style="background: linear-gradient(135deg, #002f6c 0%, #001f4d 100%); padding: 30px; text-align: center;">
              <h1 style="color: white; margin: 0; font-size: 28px;">Annuity Analysis Request</h1>
              <p style="color: #a5c9ff; margin: 10px 0 0 0; font-size: 16px;">Detailed Analysis Form Submission</p>
            </div>
            
            <div style="padding: 30px; background-color: #f9f9f9;">
              <div style="background-color: #10b981; padding: 15px; border-radius: 8px; margin-bottom: 25px;">
                <h2 style="color: white; margin: 0; font-size: 20px;">🎯 New Analysis Request Received</h2>
              </div>
              
              <div style="background-color: white; padding: 25px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                <h3 style="color: #002f6c; margin-top: 0; border-bottom: 2px solid #10b981; padding-bottom: 10px;">Customer Information</h3>
                <div style="display: grid; gap: 10px;">
                  <p style="margin: 0;"><strong>Email:</strong> <span style="color: #333;">${formData.email}</span></p>
                  <p style="margin: 0;"><strong>Submission Time:</strong> <span style="color: #333;">${new Date().toLocaleString()}</span></p>
                  <p style="margin: 0;"><strong>Form Type:</strong> <span style="color: #333;">Individual Annuity Analysis</span></p>
                </div>
              </div>
              
              <div style="background-color: white; padding: 25px; border-radius: 8px; margin-top: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                <h3 style="color: #002f6c; margin-top: 0; border-bottom: 2px solid #10b981; padding-bottom: 10px;">Annuity Details</h3>
                <div style="background-color: #f0f8ff; padding: 20px; border-radius: 6px; border-left: 4px solid #002f6c;">
                  <div style="display: grid; gap: 12px;">
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
                      <strong style="color: #555;">Company:</strong>
                      <span style="color: #002f6c; font-weight: 600;">${formData.company}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
                      <strong style="color: #555;">Product Name:</strong>
                      <span style="color: #002f6c; font-weight: 600;">${formData.productName}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
                      <strong style="color: #555;">Purchase Year:</strong>
                      <span style="color: #002f6c; font-weight: 600;">${formData.purchaseYear}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 8px 0;">
                      <strong style="color: #555;">Annuity Type:</strong>
                      <span style="color: #002f6c; font-weight: 600;">${formData.annuityType}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style="background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%); padding: 20px; border-radius: 8px; margin-top: 25px;">
                <h3 style="color: #232B3B; margin: 0 0 15px 0; font-size: 18px;">⚡ Priority Actions Required</h3>
                <ul style="color: #232B3B; margin: 0; padding-left: 20px; line-height: 1.8; font-weight: 500;">
                  <li>Run comprehensive backtesting analysis for ${formData.productName}</li>
                  <li>Calculate actual vs projected performance since ${formData.purchaseYear}</li>
                  <li>Analyze fees, caps, and participation rates impact</li>
                  <li>Prepare detailed performance report with recommendations</li>
                </ul>
              </div>
              
              <div style="background-color: #232B3B; padding: 20px; border-radius: 8px; margin-top: 20px;">
                <h4 style="color: #ffd700; margin: 0 0 10px 0;">📊 Analysis Context</h4>
                <p style="color: white; margin: 0; line-height: 1.6; font-size: 14px;">
                  Customer has owned this ${formData.annuityType} annuity for approximately ${new Date().getFullYear() - parseInt(formData.purchaseYear || String(new Date().getFullYear()))} years. 
                  Consider providing historical performance review and future optimization strategies.
                </p>
              </div>
              
              <div style="text-align: center; margin-top: 25px; padding: 20px; background-color: #e8f4f8; border-radius: 8px;">
                <p style="color: #666; margin: 0; font-size: 14px;">
                  <strong>Page Source:</strong> /analyze - Individual Annuity Analysis Tool<br>
                  <span style="font-size: 12px; color: #999;">This user is seeking detailed analysis of their existing annuity</span>
                </p>
              </div>
            </div>
          </div>
        `
      });

      // Send confirmation email to user
      await resendService.sendEmail({
        to: formData.email,
        subject: 'Your Annuity Analysis Request is Being Processed',
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <div style="background: linear-gradient(135deg, #002f6c 0%, #001f4d 100%); padding: 30px; text-align: center;">
              <h1 style="color: white; margin: 0;">Analysis Request Received</h1>
            </div>
            <div style="padding: 30px;">
              <h2 style="color: #002f6c;">Thank You for Your Request!</h2>
              <p style="color: #666; font-size: 16px; line-height: 1.6;">
                We've received your request to analyze your annuity with the following details:
              </p>
              
              <div style="background-color: #f0f8ff; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #002f6c;">
                <h3 style="color: #002f6c; margin-top: 0;">Your Annuity Information:</h3>
                <ul style="color: #555; line-height: 1.8;">
                  <li><strong>Company:</strong> ${formData.company}</li>
                  <li><strong>Product:</strong> ${formData.productName}</li>
                  <li><strong>Purchase Year:</strong> ${formData.purchaseYear}</li>
                  <li><strong>Type:</strong> ${formData.annuityType}</li>
                </ul>
              </div>
              
              <h3 style="color: #002f6c;">What We'll Analyze:</h3>
              <ul style="color: #666; line-height: 1.8;">
                <li>Historical performance vs market benchmarks</li>
                <li>Fee structure and its impact on returns</li>
                <li>Caps, spreads, and participation rates</li>
                <li>Optimization opportunities for better performance</li>
              </ul>
              
              <p style="color: #666; line-height: 1.6; margin-top: 20px;">
                <strong>Timeline:</strong> You'll receive your comprehensive analysis within 24 hours.
              </p>
              
              <p style="color: #666; line-height: 1.6;">
                <strong>Want immediate results?</strong><br>
                Call us at 800-922-1894 and we can discuss your annuity analysis right away.
              </p>
              
              <div style="text-align: center; margin-top: 30px;">
                <a href="https://annuityanswers.com" style="background-color: #ffd700; color: #002f6c; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">
                  Visit Our Website
                </a>
              </div>
            </div>
          </div>
        `
      });
    } catch (error) {
      console.error('Error sending analysis emails:', error);
      // Still navigate even if email fails
    }
    
    // Navigate to Thank You page with form data
    navigate('/thank-you', { 
      state: { 
        fromForm: true,
        formData: formData,
        email: formData.email
      } 
    });
  };

  const isFormValid = formData.company && formData.company !== 'Select a company...' && 
                      formData.productName && 
                      formData.purchaseYear && formData.purchaseYear !== 'Select a year...' && 
                      formData.annuityType &&
                      formData.email;

  return (
    <SharedLayout>
      <PageWrapper>
        <ContentSection>
          <PageTitle>Let's Look at Your Annuity</PageTitle>
          <PageSubtitle>Takes 2 minutes. No personal info required.</PageSubtitle>
          
          <FormCard>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="company">1. What company issued your annuity?</Label>
                <Select 
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                >
                  {companies.map(company => (
                    <option key={company} value={company}>{company}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="productName">2. What's the product name?</Label>
                <Input 
                  type="text"
                  id="productName"
                  placeholder="e.g., Power Index Elite Plus"
                  value={formData.productName}
                  onChange={(e) => setFormData({...formData, productName: e.target.value})}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="purchaseYear">3. When did you purchase it?</Label>
                <Select 
                  id="purchaseYear"
                  value={formData.purchaseYear}
                  onChange={(e) => setFormData({...formData, purchaseYear: e.target.value})}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>4. What type is it?</Label>
                <RadioGroup>
                  <RadioLabel>
                    <RadioInput 
                      type="radio"
                      name="annuityType"
                      value="fixed-index"
                      checked={formData.annuityType === 'fixed-index'}
                      onChange={(e) => setFormData({...formData, annuityType: e.target.value})}
                    />
                    Fixed Index Annuity
                  </RadioLabel>
                  <RadioLabel>
                    <RadioInput 
                      type="radio"
                      name="annuityType"
                      value="variable"
                      checked={formData.annuityType === 'variable'}
                      onChange={(e) => setFormData({...formData, annuityType: e.target.value})}
                    />
                    Variable Annuity
                  </RadioLabel>
                  <RadioLabel>
                    <RadioInput 
                      type="radio"
                      name="annuityType"
                      value="fixed"
                      checked={formData.annuityType === 'fixed'}
                      onChange={(e) => setFormData({...formData, annuityType: e.target.value})}
                    />
                    Fixed Annuity
                  </RadioLabel>
                  <RadioLabel>
                    <RadioInput 
                      type="radio"
                      name="annuityType"
                      value="not-sure"
                      checked={formData.annuityType === 'not-sure'}
                      onChange={(e) => setFormData({...formData, annuityType: e.target.value})}
                    />
                    Not sure
                  </RadioLabel>
                </RadioGroup>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">5. Your email (for results)</Label>
                <Input 
                  type="email"
                  id="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={!isFormValid}>
                Analyze This Annuity →
              </SubmitButton>
            </form>

            <AlternativeSection>
              <AlternativeTitle>Don't know these details?</AlternativeTitle>
              <p style={{ marginBottom: '16px', color: theme.colors.neutral.grey }}>
                Copy our Quintessential Question and ask ChatGPT:
              </p>
              <QuestionBox>
                <QuestionText>{quintessentialQuestion}</QuestionText>
                <CopyButton onClick={handleCopyQuestion} title="Copy question">
                  <CopyTooltip show={showTooltip}>Copied!</CopyTooltip>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </CopyButton>
              </QuestionBox>
            </AlternativeSection>
          </FormCard>
        </ContentSection>
      </PageWrapper>
    </SharedLayout>
  );
};

export default Analyze;